import React, { useState, useCallback } from 'react';
import { ShieldAlert, ChevronDown, ChevronRight, Home, MessageSquare, MessagesSquare, Users, Lightbulb, Zap, ClipboardList, ShieldCheck, ListChecks, Smartphone, Monitor, ChevronsLeft, Scale, Video, Image as ImageIcon, Mic, MicOff, Settings, Share2, BarChart3, ListTodo, Download, Star, Sparkles, Glasses, Hammer } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import NavContextMenu from './NavContextMenu';
import { sessionModuleTabs } from './sessionModuleTabs';

export type SidebarView =
  | 'dashboard'
  | 'transcript'
  | 'avatars'
  | 'contributions'
  | 'images'
  | 'export'
  | 'requirements'
  | 'raid'
  | 'story-deep-dive'
  | 'governance'
  | 'guidance'
  | 'decisions'
  | 'actions'
  | 'build-hints'
  | 'session-analytics'
  | 'session-graph'
  | 'tasks'
  | 'activity'
  | 'devices'
  | 'facilitator-hud'
  | 'second-screen'
  | 'meetings'
  | 'setup'
  | 'bid-extract'
  | 'bid-win-themes'
  | 'tabletop-run'
  | 'showcase'
  | 'participant-threads';

interface NavItem {
  id: SidebarView;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

interface SessionSidebarProps {
  activeView: SidebarView;
  onViewChange: (view: SidebarView) => void;
  pendingContributionsCount: number;
  pendingDecisionsCount: number;
  pendingActionsCount?: number;
  openRaidCount?: number;
  avatarCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  hasVexaFeature?: boolean;
  enabledModuleTabIds?: SidebarView[];
  hasThreadedConversationsFeature?: boolean;
  roomMicMuted?: boolean;
  onToggleRoomMic?: () => void;
  sessionId?: string;
  onAuditData?: () => void;
  /** Write or owner access. Gates write-only views such as the facilitator HUD. */
  canWrite?: boolean;
}

/** Views that require write access to the session. */
export const WRITE_ONLY_VIEWS: SidebarView[] = ['facilitator-hud'];

const topItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: Home },
];

const sessionItems: NavItem[] = [
  { id: 'avatars', label: 'Avatars', icon: Users },
  { id: 'contributions', label: 'Contributions', icon: Lightbulb },
  { id: 'export', label: 'Export', icon: Download },
  { id: 'images', label: 'Images', icon: ImageIcon },
  { id: 'setup', label: 'Setup', icon: Settings },
  { id: 'transcript', label: 'Transcript', icon: MessageSquare },
];

const captureItems: NavItem[] = [
  { id: 'actions', label: 'Actions', icon: ListTodo },
  { id: 'build-hints', label: 'Build hints', icon: Hammer },
  { id: 'decisions', label: 'Decisions', icon: Scale },
  { id: 'governance', label: 'Governance', icon: ShieldCheck },
  { id: 'guidance', label: 'Guidance', icon: Zap },
  { id: 'raid', label: 'RAID', icon: ShieldAlert },
  { id: 'requirements', label: 'Requirements', icon: ClipboardList },
];

const reportsItems: NavItem[] = [
  { id: 'session-analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'session-graph', label: 'Session Graph', icon: Share2 },
];

const baseToolsItems: NavItem[] = [
  { id: 'activity', label: 'Activity', icon: Zap },
  { id: 'devices', label: 'Devices', icon: Smartphone },
  { id: 'facilitator-hud', label: 'Facilitator HUD', icon: Glasses },
  { id: 'second-screen', label: 'Second Screen', icon: Monitor },
  { id: 'showcase', label: 'Showcase', icon: Sparkles },
  { id: 'tasks', label: 'Tasks', icon: ListChecks },
];

const allNavItems: NavItem[] = [
  ...topItems,
  ...sessionItems,
  ...captureItems,
  ...reportsItems,
  ...baseToolsItems,
  { id: 'meetings', label: 'Meetings', icon: Video },
  { id: 'participant-threads', label: 'Participants', icon: MessagesSquare },
  ...sessionModuleTabs.map((t) => ({ id: t.id, label: t.label, icon: t.icon })),
];

interface ContextMenuState {
  x: number;
  y: number;
  itemId: SidebarView;
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({
  activeView,
  onViewChange,
  pendingContributionsCount,
  pendingDecisionsCount,
  pendingActionsCount,
  openRaidCount,
  avatarCount,
  collapsed,
  onToggleCollapse,
  hasVexaFeature,
  enabledModuleTabIds,
  hasThreadedConversationsFeature,
  roomMicMuted,
  onToggleRoomMic,
  sessionId,
  onAuditData,
  canWrite = false,
}) => {
  const [sessionExpanded, setSessionExpanded] = useState(true);
  const [captureExpanded, setCaptureExpanded] = useState(true);
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const [toolsExpanded, setToolsExpanded] = useState(true);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [draggedId, setDraggedId] = useState<SidebarView | null>(null);
  const [dragOverId, setDragOverId] = useState<SidebarView | null>(null);
  const [dropAbove, setDropAbove] = useState(true);

  const { user, updateNavPreferences } = useAuth();
  const pinnedItems: SidebarView[] = (user?.navPreferences?.pinnedItems ?? []) as SidebarView[];

  const getBadge = (id: SidebarView) => {
    if (id === 'avatars') return avatarCount || undefined;
    if (id === 'contributions') return pendingContributionsCount || undefined;
    if (id === 'decisions') return pendingDecisionsCount || undefined;
    if (id === 'actions') return pendingActionsCount || undefined;
    if (id === 'raid') return openRaidCount || undefined;
    return undefined;
  };

  const handleContextMenu = useCallback((e: React.MouseEvent, id: SidebarView) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, itemId: id });
  }, []);

  const handlePin = useCallback((id: SidebarView) => {
    if (!pinnedItems.includes(id)) {
      updateNavPreferences([...pinnedItems, id]);
    }
  }, [pinnedItems, updateNavPreferences]);

  const handleUnpin = useCallback((id: SidebarView) => {
    updateNavPreferences(pinnedItems.filter(p => p !== id));
  }, [pinnedItems, updateNavPreferences]);

  const handleDragStart = useCallback((e: React.DragEvent, id: SidebarView) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: SidebarView) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDropAbove(e.clientY < rect.top + rect.height / 2);
    setDragOverId(id);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: SidebarView) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const items = [...pinnedItems];
    const fromIndex = items.indexOf(draggedId);
    const toIndex = items.indexOf(targetId);
    items.splice(fromIndex, 1);
    const insertOffset = fromIndex > toIndex ? 1 : 0;
    const insertAt = dropAbove ? toIndex : toIndex + insertOffset;
    items.splice(Math.max(0, insertAt > fromIndex ? insertAt - 1 : insertAt), 0, draggedId);
    updateNavPreferences(items);
    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId, dropAbove, pinnedItems, updateNavPreferences]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  const renderDraggableFavouriteItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = activeView === item.id;
    const badge = getBadge(item.id);
    let badgeClass: string;
    if (isActive) badgeClass = 'bg-primary text-white';
    else if (item.id === 'contributions') badgeClass = 'bg-primary text-white';
    else badgeClass = 'bg-slate-200 text-slate-600';
    const isDragging = draggedId === item.id;
    const isOver = dragOverId === item.id;
    return (
      <div
        key={item.id}
        draggable
        onDragStart={(e) => handleDragStart(e, item.id)}
        onDragOver={(e) => handleDragOver(e, item.id)}
        onDrop={(e) => handleDrop(e, item.id)}
        onDragEnd={handleDragEnd}
        className={`relative transition-opacity ${isDragging ? 'opacity-40' : 'opacity-100'}`}
      >
        {isOver && dropAbove && <div className="absolute top-0 left-2 right-2 h-0.5 bg-primary rounded-full pointer-events-none" />}
        <button
          onClick={() => onViewChange(item.id)}
          onContextMenu={(e) => handleContextMenu(e, item.id)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left w-full cursor-grab active:cursor-grabbing ${
            isActive
              ? 'bg-white border border-primary/20 shadow-sm text-primary'
              : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
          }`}
          title={collapsed ? item.label : undefined}
        >
          <Icon size={20} className={`shrink-0 ${isActive ? 'text-primary' : 'text-slate-400'}`} />
          {!collapsed && (
            <>
              <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
              {badge !== undefined && badge > 0 && (
                <span
                  className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeClass}`}
                >
                  {badge}
                </span>
              )}
            </>
          )}
        </button>
        {isOver && !dropAbove && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full pointer-events-none" />}
      </div>
    );
  };

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = activeView === item.id;
    const badge = getBadge(item.id);
    let badgeClass: string;
    if (isActive) badgeClass = 'bg-primary text-white';
    else if (item.id === 'contributions') badgeClass = 'bg-primary text-white';
    else badgeClass = 'bg-slate-200 text-slate-600';
    return (
      <button
        key={item.id}
        onClick={() => onViewChange(item.id)}
        onContextMenu={(e) => handleContextMenu(e, item.id)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left w-full ${
          isActive
            ? 'bg-white border border-primary/20 shadow-sm text-primary'
            : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
        }`}
        title={collapsed ? item.label : undefined}
      >
        <Icon size={20} className={`shrink-0 ${isActive ? 'text-primary' : 'text-slate-400'}`} />
        {!collapsed && (
          <>
            <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {item.label}
            </span>
            {badge !== undefined && badge > 0 && (
              <span
                className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeClass}`}
              >
                {badge}
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  const renderSection = (
    label: string,
    items: NavItem[],
    expanded: boolean,
    onToggle: () => void,
  ) => (
    <div className="flex flex-col gap-1">
      {!collapsed && (
        <button
          onClick={onToggle}
          className="flex items-center justify-between px-3 mb-1"
        >
          <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">{label}</h2>
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
      )}
      {(collapsed || expanded) && items.map(renderItem)}
    </div>
  );

  const favouriteNavItems = pinnedItems
    .map(id => allNavItems.find(item => item.id === id))
    .filter((item): item is NavItem => item !== undefined)
    .filter(item => canWrite || !WRITE_ONLY_VIEWS.includes(item.id));

  return (
    <>
      <aside
        className={`${
          collapsed ? 'w-16' : 'w-64'
        } bg-sidebar-bg border-r border-slate-200 flex flex-col justify-between overflow-y-auto shrink-0 z-10 transition-all duration-200`}
      >
        <div className="p-3 flex flex-col gap-6 flex-1">
          {/* Top: Dashboard */}
          <nav className="flex flex-col gap-1">
            {topItems.map(renderItem)}
          </nav>

          {/* Favourites */}
          {favouriteNavItems.length > 0 && (
            <div className="flex flex-col gap-1">
              {!collapsed && (
                <div className="flex items-center gap-1.5 px-3 mb-1">
                  <Star size={11} className="text-amber-400 shrink-0" />
                  <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Favourites</h2>
                </div>
              )}
              {favouriteNavItems.map(renderDraggableFavouriteItem)}
            </div>
          )}

          {/* Session group */}
          {renderSection(
            'Session',
            hasThreadedConversationsFeature
              ? [...sessionItems, { id: 'participant-threads' as SidebarView, label: 'Participants', icon: MessagesSquare }]
              : sessionItems,
            sessionExpanded,
            () => setSessionExpanded(!sessionExpanded)
          )}

          {/* Capture group */}
          {renderSection('Capture', captureItems, captureExpanded, () => setCaptureExpanded(!captureExpanded))}

          {/* Reports group */}
          {renderSection('Reports', reportsItems, reportsExpanded, () => setReportsExpanded(!reportsExpanded))}

          {/* Tools group */}
          <div className="flex flex-col gap-1">
            {!collapsed && (
              <button
                onClick={() => setToolsExpanded(!toolsExpanded)}
                className="flex items-center justify-between px-3 mb-1"
              >
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Tools</h2>
                {toolsExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              </button>
            )}
            {(collapsed || toolsExpanded) && (
              <>
                {renderItem({ id: 'activity', label: 'Activity', icon: Zap })}
                {onAuditData && (
                  <button
                    onClick={onAuditData}
                    title="Audit Data"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all w-full"
                  >
                    <ShieldCheck size={20} className="shrink-0 text-slate-400" />
                    {!collapsed && <span className="text-sm font-medium">Audit Data</span>}
                  </button>
                )}
                {[
                  { id: 'devices' as SidebarView, label: 'Devices', icon: Smartphone },
                  ...(canWrite ? [{ id: 'facilitator-hud' as SidebarView, label: 'Facilitator HUD', icon: Glasses }] : []),
                  ...sessionModuleTabs
                    .filter((t) => enabledModuleTabIds?.includes(t.id))
                    .map((t) => ({ id: t.id, label: t.label, icon: t.icon })),
                  ...(hasVexaFeature ? [{ id: 'meetings' as SidebarView, label: 'Meetings', icon: Video }] : []),
                  { id: 'second-screen' as SidebarView, label: 'Second Screen', icon: Monitor },
                  { id: 'showcase' as SidebarView, label: 'Showcase', icon: Sparkles },
                  { id: 'tasks' as SidebarView, label: 'Tasks', icon: ListChecks },
                ].map(renderItem)}
              </>
            )}
          </div>
        </div>

        {/* Bottom: Quick Actions + Collapse */}
        <div className="flex flex-col gap-1 p-3">
          {onToggleRoomMic && (
            <button
              onClick={onToggleRoomMic}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all w-full ${
                roomMicMuted
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
              }`}
              title={roomMicMuted ? 'Room mic is muted — click to unmute' : 'Room mic is active — click to mute'}
            >
              {roomMicMuted
                ? <MicOff size={20} className="shrink-0 text-red-400" />
                : <Mic size={20} className="shrink-0 text-slate-400" />
              }
              {!collapsed && (
                <span className="text-sm font-medium">
                  {roomMicMuted ? 'Mic Muted' : 'Room Mic'}
                </span>
              )}
            </button>
          )}
          <button
            onClick={onToggleCollapse}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all w-full"
          >
            <ChevronsLeft
              size={20}
              className={`shrink-0 text-slate-400 transition-transform ${collapsed ? 'rotate-180' : ''}`}
            />
            {!collapsed && <span className="text-sm font-medium">Collapse</span>}
          </button>
        </div>
      </aside>

      {contextMenu && (
        <NavContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          isPinned={pinnedItems.includes(contextMenu.itemId)}
          onPin={() => handlePin(contextMenu.itemId)}
          onUnpin={() => handleUnpin(contextMenu.itemId)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
};

export default SessionSidebar;
